/**
 * Routes. Every route in the sidebar resolves to a real screen — there are no dead ends.
 */

import { Navigate, Route, Routes } from 'react-router-dom'

import { AppShell } from './components/layout/app_shell'
import { ErrorState, Spinner, ToastHost } from './components/ui/primitives'
import { useApp } from './contexts/app_context'
import { DailyWorkspacePage } from './pages/home_daily_workspace'
import { MyRelationshipsPage } from './pages/home_my_relationships'
import { PersonProfilePage } from './pages/rel_person_profile'
import { ImportsPage } from './pages/data_imports'
import { CandidatesPage } from './pages/talent_candidates'
import { ResumeSearchPage } from './pages/talent_resume_search'
import {
  ActivitiesPage,
  FollowUpsPage,
  LeadsPage,
  ModuleOverview,
  NotBuiltYet,
  OpportunitiesPage,
  OrganizationsPage,
  PipelinePage,
  ReferenceAdminPage,
  SavedViewsPage,
  SearchPage,
  SettingsPage,
  TagsPage,
} from './pages/other_pages'

export default function App() {
  const { loading, error, reload } = useApp()

  if (loading) {
    return (
      <div style={{ padding: 40 }}>
        <Spinner />
      </div>
    )
  }

  if (error) {
    return (
      <div className="tf-error-page">
        <ErrorState
          title="Tidalfin could not start"
          message={error}
          technical="The BFF could not be reached, or its configuration is missing. Check that the application server is running on port 8041 and that tfwebapp_configstore is present."
          onRetry={() => void reload()}
        />
      </div>
    )
  }

  return (
    <>
      <AppShell>
        <Routes>
          <Route path="/" element={<Navigate to="/home" replace />} />

          {/* Home */}
          <Route path="/home" element={<DailyWorkspacePage />} />
          <Route path="/home/my-relationships" element={<MyRelationshipsPage />} />

          {/* Relationships */}
          <Route
            path="/relationships"
            element={
              <ModuleOverview
                crumb="Tidalfin / Relationships"
                title="Relationships"
                description="People, organizations, leads and opportunities — and where every relationship stands."
                cards={[
                  { name: 'People', icon: 'person', to: '/relationships/people', desc: 'Everyone Dhansol knows. A person can hold several relationship types at once.' },
                  { name: 'Organizations', icon: 'business', to: '/relationships/organizations', desc: 'Accounts with their contacts, so an account is one relationship rather than several unconnected people.' },
                  { name: 'Leads', icon: 'filter_alt', to: '/relationships/leads', desc: 'Potential business relationships that still need qualification.' },
                  { name: 'Opportunities', icon: 'trending_up', to: '/relationships/opportunities', desc: 'Value, probability, stage and the next action that moves each one.' },
                  { name: 'Pipeline', icon: 'view_kanban', to: '/relationships/pipeline', desc: 'The same opportunities by stage. Every card shows its next action.' },
                ]}
              />
            }
          />
          <Route path="/relationships/people" element={<MyRelationshipsPage />} />
          <Route path="/relationships/people/:contactId" element={<PersonProfilePage />} />
          <Route path="/relationships/organizations" element={<OrganizationsPage />} />
          <Route path="/relationships/leads" element={<LeadsPage />} />
          <Route path="/relationships/opportunities" element={<OpportunitiesPage />} />
          <Route path="/relationships/pipeline" element={<PipelinePage />} />

          {/* Talent */}
          <Route
            path="/talent"
            element={
              <ModuleOverview
                crumb="Tidalfin / Talent"
                title="Talent"
                description="Candidates, consultants and resumes. The same Person record — a consultant placed today may be a client contact in two years."
                cards={[
                  { name: 'Candidates', icon: 'badge', to: '/talent/candidates', desc: 'Talent profiles over the Person record: skills, availability, rate and candidate status.' },
                  { name: 'Resume Search', icon: 'manage_search', to: '/talent/resume-search', desc: 'Contextual search over resume text, with the matching passage shown for every result.' },
                ]}
              />
            }
          />
          <Route path="/talent/candidates" element={<CandidatesPage />} />
          <Route path="/talent/resume-search" element={<ResumeSearchPage />} />

          {/* Engagement */}
          <Route
            path="/engagement"
            element={
              <ModuleOverview
                crumb="Tidalfin / Engagement"
                title="Engagement"
                description="Every interaction, commitment and piece of organized outreach."
                cards={[
                  { name: 'Follow-Ups', icon: 'event_upcoming', to: '/engagement/follow-ups', desc: 'Your work queue, bucketed by when. Overdue first.' },
                  { name: 'Activities', icon: 'history', to: '/engagement/activities', desc: 'Every call, email and meeting. Direction matters.' },
                  { name: 'Campaigns', icon: 'campaign', to: '/engagement/campaigns', desc: 'Organized outreach with a safe-outreach review that cannot be skipped. Phase 3.' },
                ]}
              />
            }
          />
          <Route path="/engagement/follow-ups" element={<FollowUpsPage />} />
          <Route path="/engagement/activities" element={<ActivitiesPage />} />
          <Route
            path="/engagement/campaigns"
            element={
              <NotBuiltYet
                crumb="Tidalfin / Engagement / Campaigns"
                title="Campaigns"
                phase="Phase 3"
                description="The safe-outreach data is already accumulating: outreach recency, Do Not Contact and waiting state are maintained from Phase 1, and the risk check endpoint is live. A suppression list built the day campaigns launch is one with no data in it — this one will not be."
              />
            }
          />

          {/* Data */}
          <Route
            path="/data"
            element={
              <ModuleOverview
                crumb="Tidalfin / Data"
                title="Data"
                description="Find anything, and keep the views you use every day."
                cards={[
                  { name: 'Search', icon: 'manage_search', to: '/data/search', desc: 'Search people, organizations, notes and activities. Every result explains why it matched.' },
                  { name: 'Saved Views', icon: 'bookmark', to: '/data/saved-views', desc: 'Filters you keep and pin.' },
                  { name: 'Imports', icon: 'upload_file', to: '/data/imports', desc: 'CSV and Excel import with a review step. Phase 1 remaining work.' },
                ]}
              />
            }
          />
          <Route path="/data/search" element={<SearchPage />} />
          <Route path="/data/saved-views" element={<SavedViewsPage />} />
          <Route path="/data/imports" element={<ImportsPage />} />

          {/* Administration */}
          <Route
            path="/admin"
            element={
              <ModuleOverview
                crumb="Tidalfin / Administration"
                title="Administration"
                description="Everything here is configuration — no code change is needed to add a lead status."
                cards={[
                  { name: 'Categories & Statuses', icon: 'category', to: '/admin/reference', desc: 'Every configurable list. The indicator column holds a token name, never a colour.' },
                  { name: 'Tags', icon: 'sell', to: '/admin/tags', desc: 'Free labels that complement the structured fields.' },
                  { name: 'Settings', icon: 'tune', to: '/admin/settings', desc: 'Account, environment and safe-outreach windows.' },
                ]}
              />
            }
          />
          <Route path="/admin/reference" element={<ReferenceAdminPage />} />
          <Route path="/admin/tags" element={<TagsPage />} />
          <Route path="/admin/settings" element={<SettingsPage />} />

          {/* A broken in-app route shows a copyable error, never a silent bounce (BlueHomer §3.33) */}
          <Route
            path="*"
            element={
              <div className="body-scroll">
                <ErrorState
                  title="That page does not exist"
                  message="The route you followed is not part of Tidalfin."
                  technical={typeof window !== 'undefined' ? window.location.pathname : ''}
                />
              </div>
            }
          />
        </Routes>
      </AppShell>
      <ToastHost />
    </>
  )
}
